import React, { useState } from "react";
import { Search, Download, Instagram, Facebook, Link2 } from "lucide-react";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { asText, CardShell, EmptyRow, formatCompact, formatCurrency, formatPct, formatRoas, LoadingRow, unwrapValue, useCxoResource } from "../shared";

// Generic Placements tab — identical shape at every level, confirmed by the
// doc's repeated description: "highlights (top placement, highest CTR,
// lowest CPA, best converting), reach and results per placement,
// results_share_pct for the donut, and the table rows (publisher_platform,
// platform_position, ...), exactly as Meta names them."

const DONUT_COLORS = ["#4f46e5", "#60a5fa", "#f59e0b", "#a855f7", "#10b981", "#f43f5e"];
const PLATFORM_ICON: Record<string, { Icon: React.ComponentType<any>; className: string }> = {
  instagram: { Icon: Instagram, className: "text-pink-500" },
  facebook: { Icon: Facebook, className: "text-blue-600" },
  audience_network: { Icon: Link2, className: "text-purple-500" },
  messenger: { Icon: Link2, className: "text-sky-500" },
};

const PlacementsTab: React.FC<{ basePath: string; entityId: string; baseParams: Record<string, any>; exportPath?: string }> = ({ basePath, entityId, baseParams, exportPath }) => {
  const [search, setSearch] = useState("");
  const [platform, setPlatform] = useState("All");
  const { data, loading } = useCxoResource<any>(basePath, `${entityId}/placements`, baseParams, !!entityId);

  const items: any[] = Array.isArray(data?.items) ? data.items : [];
  const filtered = items.filter(
    (it) =>
      (platform === "All" || asText(it.publisher_platform).toLowerCase() === platform.toLowerCase()) &&
      (!search || asText(it.platform_position ?? it.placement).toLowerCase().includes(search.toLowerCase()))
  );
  const platforms = Array.from(new Set(items.map((it) => asText(it.publisher_platform)))).filter((p) => p !== "—");
  const highlights = data?.highlights || {};

  const handleExport = () => {
    if (!exportPath) return;
    const token = localStorage.getItem("authToken");
    const base = process.env.REACT_APP_BACKEND_URL || "https://api.connect.dalmlabs.com";
    const params = new URLSearchParams(baseParams);
    window.open(`${base}${exportPath}?${params.toString()}&access_token=${encodeURIComponent(token || "")}`, "_blank");
  };

  if (loading && !data) return <LoadingRow label="Loading placements…" />;
  if (!data || items.length === 0) return <EmptyRow label="No placement data for this period yet." />;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: "Top placement", raw: highlights.top_placement, sub: (v: any) => (v?.results != null ? `${formatCompact(v.results)} results` : null) },
          { label: "Highest CTR", raw: highlights.highest_ctr, sub: (v: any) => (v?.ctr != null ? formatPct(v.ctr) : null) },
          { label: "Lowest CPA", raw: highlights.lowest_cpa, sub: (v: any) => (v?.cost_per_result != null ? formatCurrency(v.cost_per_result) : null) },
          { label: "Best converting placement", raw: highlights.best_converting, sub: (v: any) => (v?.roas != null ? `${formatRoas(v.roas)} ROAS` : null) },
        ].map((c) => (
          <div key={c.label} className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-4">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">{c.label}</p>
            <p className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">{asText(c.raw?.name ?? c.raw?.platform_position ?? c.raw)}</p>
            {c.sub(c.raw) && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{c.sub(c.raw)}</p>}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CardShell title="Reach and conversions by placement" description="Compare reach and results across Meta placements.">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={items.map((it) => ({ name: asText(it.platform_position ?? it.placement), reach: unwrapValue(it.reach) || 0, results: unwrapValue(it.results) || 0 }))} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="reach" name="Reach" fill="#60a5fa" radius={[4, 4, 0, 0]} />
                <Bar dataKey="results" name="Results" fill="#a855f7" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardShell>

        <CardShell title="Results share by placement" description="Share of total results by placement.">
          <div className="flex items-center gap-4">
            <div className="h-44 w-44 shrink-0 relative">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={items} dataKey={(d: any) => unwrapValue(d.results) || 0} nameKey={(d: any) => asText(d.platform_position ?? d.placement)} innerRadius={42} outerRadius={66} paddingAngle={2}>
                    {items.map((_, i) => (
                      <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-base font-bold text-gray-900 dark:text-gray-100">{formatCompact(data?.total_results ?? items.reduce((s, it) => s + (Number(unwrapValue(it.results)) || 0), 0), "0")}</span>
                <span className="text-[10px] text-gray-400 dark:text-gray-500">Results</span>
              </div>
            </div>
            <div className="space-y-1.5 text-xs flex-1 min-w-0">
              {items.map((it, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                  <span className="text-gray-600 dark:text-gray-300 truncate flex-1">{asText(it.platform_position ?? it.placement)}</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100 shrink-0">{(() => { const v = unwrapValue(it.results_share_pct); return v != null ? `${Number(v).toFixed(0)}%` : "0%"; })()}</span>
                </div>
              ))}
            </div>
          </div>
        </CardShell>
      </div>

      <CardShell
        title="Placement performance"
        action={
          exportPath && (
            <button type="button" onClick={handleExport} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800">
              <Download size={13} /> Export
            </button>
          )
        }
      >
        <div className="flex flex-col sm:flex-row gap-2 mb-3">
          <div className="relative flex-1 min-w-[180px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search placements..." className="w-full pl-8 pr-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 placeholder-gray-400" />
          </div>
          <select value={platform} onChange={(e) => setPlatform(e.target.value)} className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-700 dark:text-gray-200 capitalize">
            <option value="All">Platform: All</option>
            {platforms.map((p) => (
              <option key={p} value={p}>
                Platform: {p}
              </option>
            ))}
          </select>
        </div>
        <div className="overflow-x-auto -mx-1">
          <table className="w-full min-w-[760px] text-sm">
            <thead>
              <tr className="text-left text-[11px] font-semibold text-gray-400 dark:text-gray-500 tracking-wide">
                <th className="pb-2 pl-1">PLACEMENT</th>
                <th className="pb-2">PLATFORM</th>
                <th className="pb-2">SPEND</th>
                <th className="pb-2">IMPRESSIONS</th>
                <th className="pb-2">CLICKS</th>
                <th className="pb-2">CTR</th>
                <th className="pb-2">RESULTS</th>
                <th className="pb-2">COST PER RESULT</th>
                <th className="pb-2 pr-1 text-right">ROAS</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it, i) => {
                const platformKey = asText(it.publisher_platform).toLowerCase();
                const icon = PLATFORM_ICON[platformKey];
                const Icon = icon?.Icon || Link2;
                return (
                  <tr key={i} className="border-t border-gray-50 dark:border-gray-800">
                    <td className="py-2.5 pl-1">
                      <span className="flex items-center gap-2 font-medium text-gray-800 dark:text-gray-100 whitespace-nowrap">
                        <Icon size={14} className={icon?.className || "text-gray-400"} /> {asText(it.platform_position ?? it.placement)}
                      </span>
                    </td>
                    <td className="py-2.5 text-gray-600 dark:text-gray-300 capitalize">{asText(it.publisher_platform)}</td>
                    <td className="py-2.5 text-gray-600 dark:text-gray-300">{formatCurrency(unwrapValue(it.spend ?? it.metrics?.spend), "₦0.00")}</td>
                    <td className="py-2.5 text-gray-600 dark:text-gray-300">{formatCompact(unwrapValue(it.impressions ?? it.metrics?.impressions), "0")}</td>
                    <td className="py-2.5 text-gray-600 dark:text-gray-300">{formatCompact(unwrapValue(it.clicks ?? it.metrics?.clicks), "0")}</td>
                    <td className="py-2.5 text-gray-600 dark:text-gray-300">{formatPct(unwrapValue(it.ctr ?? it.metrics?.ctr), "0%")}</td>
                    <td className="py-2.5 text-gray-600 dark:text-gray-300">{formatCompact(unwrapValue(it.results ?? it.metrics?.results), "0")}</td>
                    <td className="py-2.5 text-gray-600 dark:text-gray-300">{formatCurrency(unwrapValue(it.cost_per_result ?? it.metrics?.cost_per_result), "₦0.00")}</td>
                    <td className="py-2.5 pr-1 text-right font-semibold text-emerald-600 dark:text-emerald-400">{formatRoas(unwrapValue(it.roas ?? it.metrics?.roas), "N/A")}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardShell>
    </div>
  );
};

export default PlacementsTab;
